'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Search, DollarSign, Package, TrendingUp, RefreshCw, Bug, Trash2,
  Database, Activity, Globe, Tag, Hash, FileText, Upload, FolderOpen,
  Boxes, Filter, X, CheckCircle2, AlertCircle,
} from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { useToast } from '@/hooks/useToast'
import { formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FunctionsService } from '@/services/functions.service'
import { rtdb } from '@/config/firebase'
import { ref, get, set } from 'firebase/database'

interface Product {
  id: string
  title: string
  inventory: number
  cost?: number
  imageUrl?: string | null
  handle?: string | null
  status?: string
  collections?: string[]
  createdAt?: string | null
  // Datos completos del JSON de Shopify
  variants?: any[]
  options?: any[]
  images?: any[]
  body_html?: string
  vendor?: string
  product_type?: string
  tags?: string
}

interface Collection {
  id: string
  title: string
  productsCount: number
  totalInventory: number
  totalCost: number
}

function CostsDashboard() {
  const { toast } = useToast()
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'collections' | 'products'>('collections')
  const [selectedCollection, setSelectedCollection] = useState<string>('all')
  const [isDebugOpen, setIsDebugOpen] = useState(false)
  const [debugProductSearch, setDebugProductSearch] = useState('')
  const [debugProductData, setDebugProductData] = useState<any>(null)
  const [isDebugLoading, setIsDebugLoading] = useState(false)

  // Filtros adicionales
  const [costFilter, setCostFilter] = useState<'all' | 'with-cost' | 'without-cost'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all')

  const [isSyncing, setIsSyncing] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [lastSync, setLastSync] = useState<Date | null>(null)
  const [dataSource, setDataSource] = useState<'firestore' | 'realtime' | 'api'>('firestore')
  const [sourceInfo, setSourceInfo] = useState<string>('')

  // Búsqueda específica por colección o producto
  const [searchType, setSearchType] = useState<'collection' | 'product'>('collection')
  const [searchValue, setSearchValue] = useState('')
  const [searchIdType, setSearchIdType] = useState<'id' | 'title'>('title')
  const [searchResults, setSearchResults] = useState<Product[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Limpiar caché de Firestore
  const clearCache = useCallback(async () => {
    setIsClearing(true)
    try {
      toast({
        title: 'Limpiando caché',
        description: 'Eliminando productos del caché...',
      })

      const response = await FunctionsService.clearProductsCache()

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive',
        })
        return
      }

      if (response.data?.success) {
        toast({
          title: 'Caché limpiado',
          description: `${response.data.deletedProducts} productos eliminados del caché`,
        })

        // Limpiar estado local
        setProducts([])
        setLastSync(null)
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al limpiar caché',
        variant: 'destructive',
      })
    } finally {
      setIsClearing(false)
    }
  }, [toast])

  // Cargar productos desde Firestore, Realtime DB o Shopify API
  const loadProducts = useCallback(async () => {
    setIsLoading(true)
    const startTime = Date.now()

    try {

      let productsData: Product[] = []
      let sourceDesc = ''

      if (dataSource === 'realtime') {
        // Obtener desde Realtime Database
        console.log('📊 Loading from Realtime Database...')
        toast({
          title: 'Cargando desde Realtime DB',
          description: 'Obteniendo datos cacheados...',
          duration: 2000,
        })

        const cacheRef = ref(rtdb, 'products_cache')
        const cacheSnapshot = await get(cacheRef)

        if (cacheSnapshot.exists()) {
          const cached = cacheSnapshot.val()

          // Convertir el objeto de productos a array
          const productsMap = cached.products as Record<string, Product> | Product[]
          if (Array.isArray(productsMap)) {
            // Si es array viejo, usar tal cual
            productsData = productsMap
          } else {
            // Si es objeto nuevo, convertir a array
            productsData = Object.values(productsMap)
          }

          const cacheAge = Date.now() - (cached.timestamp || 0)

          setLastSync(new Date(cached.timestamp))
          sourceDesc = `Realtime DB (${Math.floor(cacheAge / 1000)}s de antigüedad)`

          console.log(`✅ Loaded ${productsData.length} products from Realtime DB`)
          console.log(`📦 Sample product:`, productsData[0])

          // Buscar el producto específico que estás depurando
          const debugProduct = productsData.find(p => p.id === '7970902278233' || p.title.includes('Pure Knockout Jacket Brown'))
          if (debugProduct) {
            console.log(`🔍 DEBUG - Pure Knockout Jacket from Realtime DB:`, {
              id: debugProduct.id,
              title: debugProduct.title,
              cost: debugProduct.cost,
              inventory: debugProduct.inventory,
            })
          }
        } else {
          toast({
            title: 'Realtime DB vacía',
            description: 'No hay datos en caché. Usa el modo API primero.',
            variant: 'destructive',
          })
          setIsLoading(false)
          return
        }

      } else if (dataSource === 'api') {
        // Obtener desde Shopify API
        console.log('🌐 Loading from Shopify API...')
        toast({
          title: 'Cargando desde Shopify API',
          description: 'Obteniendo productos con costos en tiempo real. Esto puede tardar varios minutos...',
          duration: 5000,
        })

        const response = await FunctionsService.getAllProducts()

        if (response.error || !response.data) {
          toast({
            title: 'Error al cargar desde API',
            description: response.error || 'No se recibieron datos',
            variant: 'destructive',
          })
          setIsLoading(false)
          return
        }

        productsData = response.data as Product[]
        setLastSync(new Date())
        sourceDesc = 'Shopify API (Tiempo real)'

        // Guardar en Realtime DB para uso futuro con estructura de objeto (no array)
        const cacheRef = ref(rtdb, 'products_cache')
        const productsMap = productsData.reduce((acc, product) => {
          acc[product.id] = product
          return acc
        }, {} as Record<string, Product>)

        await set(cacheRef, {
          products: productsMap,
          timestamp: Date.now(),
        })

        console.log(`✅ Loaded ${productsData.length} products from Shopify API`)
        console.log(`💾 Saved to Realtime DB cache as object with IDs as keys`)
        console.log(`📦 Sample product:`, productsData[0])

        // Buscar el producto específico que estás depurando
        const debugProduct = productsData.find(p => p.id === '7970902278233' || p.title.includes('Pure Knockout Jacket Brown'))
        if (debugProduct) {
          console.log(`🔍 DEBUG - Pure Knockout Jacket from API:`, {
            id: debugProduct.id,
            title: debugProduct.title,
            cost: debugProduct.cost,
            inventory: debugProduct.inventory,
            variantsCount: debugProduct.variants?.length,
            variantCosts: debugProduct.variants?.map((v: any) => ({
              title: v.title,
              cost: v.cost,
              inventory: v.inventory_quantity,
            })),
          })
          console.log(`📦 Full product data:`, debugProduct)
        }

      } else {
        // Obtener desde Firestore
        console.log('🔥 Loading from Firestore...')
        toast({
          title: 'Cargando desde Firestore',
          description: 'Obteniendo datos del caché...',
          duration: 2000,
        })

        const response = await FunctionsService.getProductsFromFirestore()

        if (response.error || !response.data) {
          toast({
            title: 'Error al cargar desde Firestore',
            description: response.error || 'No se recibieron datos',
            variant: 'destructive',
          })
          setIsLoading(false)
          return
        }

        productsData = response.data.products as Product[]
        setLastSync(response.data.metadata.lastSync)
        sourceDesc = 'Firestore (Caché)'

        console.log(`✅ Loaded ${productsData.length} products from Firestore`)
        console.log(`📦 Sample product:`, productsData[0])

        // Buscar el producto específico que estás depurando
        const debugProduct = productsData.find(p => p.id === '7970902278233' || p.title.includes('Pure Knockout Jacket Brown'))
        if (debugProduct) {
          console.log(`🔍 DEBUG - Pure Knockout Jacket from Firestore:`, {
            id: debugProduct.id,
            title: debugProduct.title,
            cost: debugProduct.cost,
            inventory: debugProduct.inventory,
          })
        }
      }

      setProducts(productsData)

      const loadTime = Date.now() - startTime
      const productsWithCost = productsData.filter(p => (p.cost || 0) > 0).length

      setSourceInfo(`${sourceDesc} | ${productsData.length} productos | ${productsWithCost} con costo | ${loadTime}ms`)

      console.log(`⏱️ Load time: ${loadTime}ms`)
      console.log(`💰 Products with cost: ${productsWithCost}/${productsData.length}`)

      toast({
        title: 'Productos cargados',
        description: `${productsData.length} productos desde ${sourceDesc} (${loadTime}ms)`,
        duration: 3000,
      })
    } catch (error: any) {
      console.error('❌ Error loading products:', error)
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron cargar los productos',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast, dataSource])

  // Sincronizar productos desde Shopify a Firestore
  const syncProducts = useCallback(async () => {
    setIsSyncing(true)
    try {

      toast({
        title: 'Sincronización iniciada',
        description: 'Obteniendo productos y costos desde Shopify. Esto puede tardar hasta 9 minutos...',
        duration: 10000,
      })

      const response = await FunctionsService.syncProductsToFirestore()

      if (response.error) {
        const errorMessage = response.error.includes('deadline-exceeded')
          ? 'La sincronización tardó demasiado. Intenta limpiar el caché primero y luego sincronizar nuevamente.'
          : response.error

        toast({
          title: 'Error al sincronizar',
          description: errorMessage,
          variant: 'destructive',
          duration: 8000,
        })
        return
      }

      if (response.data?.success) {
        toast({
          title: 'Sincronización completada',
          description: `${response.data.totalProducts} productos sincronizados con ${response.data.productsWithCost} productos con costo definido`,
          duration: 5000,
        })

        // Recargar productos después de sincronizar
        await loadProducts()
      }
    } catch (error: any) {
      const errorMessage = error.message?.includes('deadline-exceeded')
        ? 'La sincronización tardó demasiado. Intenta limpiar el caché primero y luego sincronizar nuevamente.'
        : error.message || 'Error al sincronizar productos'

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
        duration: 8000,
      })
    } finally {
      setIsSyncing(false)
    }
  }, [toast, loadProducts])

  // Depurar producto específico
  const debugSpecificProduct = useCallback(async () => {
    if (!debugProductSearch.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa el título o ID del producto',
        variant: 'destructive',
      })
      return
    }

    setIsDebugLoading(true)
    try {

      // Buscar en Firestore primero
      const firestoreProduct = products.find(
        (p) =>
          p.title.toLowerCase().includes(debugProductSearch.toLowerCase()) ||
          p.id === debugProductSearch
      )

      // Obtener del API de Shopify
      let shopifyProduct = null
      try {
        const response = await FunctionsService.getProductById(debugProductSearch)
        if (response.data) {
          shopifyProduct = response.data
        }
      } catch (error) {
        console.warn('No se pudo obtener el producto desde Shopify:', error)
      }

      setDebugProductData({
        search: debugProductSearch,
        foundInFirestore: !!firestoreProduct,
        firestoreData: firestoreProduct || null,
        shopifyData: shopifyProduct || null,
        timestamp: new Date().toISOString(),
      })

      toast({
        title: 'Debug completado',
        description: `Producto ${firestoreProduct ? 'encontrado' : 'no encontrado'} en Firestore`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al depurar producto',
        variant: 'destructive',
      })
    } finally {
      setIsDebugLoading(false)
    }
  }, [debugProductSearch, products, toast])

  // Importar productos automáticamente desde Shopify
  const handleImportFromShopify = useCallback(async () => {
    setIsDebugLoading(true)

    try {
      console.log('🚀 Obteniendo productos desde Shopify...')

      toast({
        title: 'Importación iniciada',
        description: 'Obteniendo productos desde Shopify. Esto puede tardar varios minutos...',
        duration: 10000,
      })


      // Obtener productos desde Shopify (ya tiene los secretos configurados)
      const productsResponse = await FunctionsService.getAllProducts()

      if (productsResponse.error || !productsResponse.data) {
        throw new Error(productsResponse.error || 'No se recibieron datos')
      }

      const productsToImport = productsResponse.data

      console.log(`📦 Obtenidos ${productsToImport.length} productos de Shopify`)
      console.log(`💾 Guardando en Firestore...`)

      // Importar los productos obtenidos
      const result = await FunctionsService.importProductsFromJSON(productsToImport)

      if (result.error) {
        throw new Error(result.error)
      }

      if (result.data) {
        const data = result.data
        setDebugProductData((prev: any) => ({
          ...prev,
          importStatus: {
            success: true,
            message: 'Importación automática completada exitosamente',
            details: {
              totalProducts: data.totalProducts,
              productsWithCost: data.productsWithCost,
            },
          },
        }))

        toast({
          title: 'Importación completada',
          description: `${data.totalProducts} productos importados con ${data.productsWithCost} productos con costo`,
          duration: 8000,
        })

        // Recargar productos desde Firestore
        await loadProducts()
      }
    } catch (error: any) {
      console.error('Error en importación automática:', error)

      setDebugProductData((prev: any) => ({
        ...prev,
        importStatus: {
          success: false,
          message: error.message || 'Error al importar productos desde Shopify',
          details: null,
        },
      }))

      toast({
        title: 'Error en importación',
        description: error.message || 'Error al importar productos',
        variant: 'destructive',
        duration: 8000,
      })
    } finally {
      setIsDebugLoading(false)
    }
  }, [toast, loadProducts])

  // Búsqueda específica por colección o producto
  const handleSpecificSearch = useCallback(async () => {
    if (!searchValue.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa un valor de búsqueda',
        variant: 'destructive',
      })
      return
    }

    setIsSearching(true)
    setHasSearched(true)
    setSearchResults([])

    try {

      if (searchType === 'collection') {
        toast({
          title: 'Buscando productos',
          description: `Buscando productos en la colección...`,
          duration: 5000,
        })

        const searchParams = searchIdType === 'id'
          ? { collectionId: searchValue }
          : { collectionTitle: searchValue }

        const result = await FunctionsService.getProductsByCollection(searchParams)

        if (result.error) {
          throw new Error(result.error)
        }

        if (result.data) {
          setSearchResults(result.data.products || [])
          toast({
            title: 'Búsqueda completada',
            description: `${result.data.totalProducts} productos encontrados (${result.data.productsWithCost} con costos)`,
          })
        }
      } else {
        // Búsqueda por producto individual
        toast({
          title: 'Buscando producto',
          description: 'Obteniendo detalles del producto...',
          duration: 5000,
        })

        const result = await FunctionsService.getProductById(searchValue)

        if (result.error) {
          throw new Error(result.error)
        }

        if (result.data) {
          setSearchResults([result.data])
          toast({
            title: 'Producto encontrado',
            description: `${result.data.title} - Costo: ${formatCurrency(result.data.cost || 0)}`,
          })
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error en búsqueda',
        description: error.message || 'Error al buscar',
        variant: 'destructive',
        duration: 8000,
      })
    } finally {
      setIsSearching(false)
    }
  }, [searchValue, searchType, searchIdType, toast])

  // Importar productos desde JSON
  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      setIsDebugLoading(true)

      try {
        const reader = new FileReader()

        reader.onload = async (event) => {
          try {
            const jsonText = event.target?.result as string
            const productsToImport = JSON.parse(jsonText)

            if (!Array.isArray(productsToImport)) {
              throw new Error('El archivo JSON debe contener un array de productos')
            }

            console.log(`📦 Importando ${productsToImport.length} productos...`)

            toast({
              title: 'Importación iniciada',
              description: `Importando ${productsToImport.length} productos. Esto puede tardar varios minutos...`,
              duration: 10000,
            })

            const result = await FunctionsService.importProductsFromJSON(productsToImport)

            if (result.error) {
              throw new Error(result.error)
            }

            if (result.data) {
              const data = result.data
              setDebugProductData((prev: any) => ({
                ...prev,
                importStatus: {
                  success: true,
                  message: data.message || 'Importación completada exitosamente',
                  details: {
                    totalProducts: data.totalProducts,
                    productsWithCost: data.productsWithCost,
                  },
                },
              }))

              toast({
                title: 'Importación completada',
                description: `${data.totalProducts} productos importados con ${data.productsWithCost} productos con costo`,
                duration: 8000,
              })

              // Recargar productos desde Firestore
              await loadProducts()
            }
          } catch (parseError: any) {
            console.error('Error procesando JSON:', parseError)

            setDebugProductData((prev: any) => ({
              ...prev,
              importStatus: {
                success: false,
                message: parseError.message || 'Error al importar productos',
                details: null,
              },
            }))

            toast({
              title: 'Error en importación',
              description: parseError.message || 'Error al procesar el archivo JSON',
              variant: 'destructive',
              duration: 8000,
            })
          }
        }

        reader.onerror = () => {
          toast({
            title: 'Error',
            description: 'No se pudo leer el archivo',
            variant: 'destructive',
          })
        }

        reader.readAsText(file)
      } catch (error: any) {
        console.error('Error al importar:', error)

        setDebugProductData((prev: any) => ({
          ...prev,
          importStatus: {
            success: false,
            message: error.message || 'Error al importar productos',
            details: null,
          },
        }))

        toast({
          title: 'Error',
          description: error.message || 'Error al importar productos',
          variant: 'destructive',
        })
      } finally {
        setIsDebugLoading(false)
      }
    }

    input.click()
  }, [toast, loadProducts])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // Filtrar productos según filtros activos
  const filteredProductsBase = useMemo(() => {
    let filtered = products

    // Filtro por costo
    if (costFilter === 'with-cost') {
      filtered = filtered.filter(p => (p.cost || 0) > 0)
    } else if (costFilter === 'without-cost') {
      filtered = filtered.filter(p => (p.cost || 0) === 0)
    }

    // Filtro por estado
    if (statusFilter === 'active') {
      filtered = filtered.filter(p => p.status === 'active')
    } else if (statusFilter === 'archived') {
      filtered = filtered.filter(p => p.status === 'archived')
    }

    return filtered
  }, [products, costFilter, statusFilter])

  // Agrupar productos por colección
  const productsByCollection = useMemo(() => {
    const grouped = new Map<string, Product[]>()

    filteredProductsBase.forEach((product) => {
      if (!product.collections || product.collections.length === 0) {
        // Productos sin colección
        if (!grouped.has('Sin colección')) {
          grouped.set('Sin colección', [])
        }
        grouped.get('Sin colección')!.push(product)
      } else {
        // Productos con colecciones
        product.collections.forEach((collection) => {
          if (!grouped.has(collection)) {
            grouped.set(collection, [])
          }
          grouped.get(collection)!.push(product)
        })
      }
    })

    return grouped
  }, [filteredProductsBase])

  // Calcular métricas de colecciones
  const collectionsData = useMemo(() => {
    const data: Collection[] = []

    productsByCollection.forEach((products, collectionName) => {
      const totalInventory = products.reduce((sum, p) => sum + (p.inventory || 0), 0)
      const totalCost = products.reduce((sum, p) => sum + ((p.inventory || 0) * (p.cost || 0)), 0)

      data.push({
        id: collectionName,
        title: collectionName,
        productsCount: products.length,
        totalInventory,
        totalCost,
      })
    })

    return data.sort((a, b) => b.totalCost - a.totalCost)
  }, [productsByCollection])

  // Filtrar datos según búsqueda y selección
  const filteredCollections = useMemo(() => {
    return collectionsData.filter((collection) =>
      collection.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [collectionsData, searchTerm])

  const filteredProducts = useMemo(() => {
    let filtered = filteredProductsBase

    // Filtrar por colección seleccionada
    if (selectedCollection !== 'all') {
      filtered = productsByCollection.get(selectedCollection) || []
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter((product) =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }, [filteredProductsBase, productsByCollection, selectedCollection, searchTerm])

  // Calcular totales basados en productos filtrados (incluye búsqueda y selección)
  const totalInventoryValue = useMemo(() => {
    return filteredProducts.reduce((sum, p) => sum + ((p.inventory || 0) * (p.cost || 0)), 0)
  }, [filteredProducts])

  const totalProducts = filteredProducts.length
  const totalInventoryUnits = filteredProducts.reduce((sum, p) => sum + (p.inventory || 0), 0)
  const averageCostPerUnit = totalInventoryUnits > 0 ? totalInventoryValue / totalInventoryUnits : 0

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.015]">
        <img src="/nibpr.svg" alt="NIBPR Marca de agua" className="h-96 w-auto select-none" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-10">
        <Header />

        {/* Control Card */}
        <Card className="mt-8 border-white/10 bg-white/5 text-white backdrop-blur">
          <CardContent className="p-4 space-y-3">
            {/* Row 1: Data source */}
            <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
              <span className="flex-shrink-0 text-xs text-white/40 uppercase tracking-wide pr-1">Fuente:</span>
              <Button size="sm" variant="outline"
                onClick={() => { setDataSource('firestore'); toast({ title: 'Modo Firestore activado', description: 'Datos desde caché Firestore (rápido)', duration: 3000 }) }}
                className={`flex-shrink-0 gap-1.5 ${dataSource === 'firestore' ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                <Database className="h-3.5 w-3.5" /> Firestore
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => { setDataSource('realtime'); toast({ title: 'Modo Realtime DB activado', description: 'Datos desde Realtime Database', duration: 3000 }) }}
                className={`flex-shrink-0 gap-1.5 ${dataSource === 'realtime' ? 'bg-purple-500 text-white border-purple-500 hover:bg-purple-600' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                <Activity className="h-3.5 w-3.5" /> Realtime DB
              </Button>
              <Button size="sm" variant="outline"
                onClick={() => { setDataSource('api'); toast({ title: 'Modo Shopify API activado', description: 'Datos en tiempo real desde Shopify (lento)', duration: 3000 }) }}
                className={`flex-shrink-0 gap-1.5 ${dataSource === 'api' ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                <Globe className="h-3.5 w-3.5" /> Shopify API
              </Button>
              {lastSync && dataSource === 'firestore' && (
                <span className="flex-shrink-0 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white/40 whitespace-nowrap">
                  Sync: {new Date(lastSync).toLocaleString('es-ES')}
                </span>
              )}
              {sourceInfo && (
                <span className="flex-shrink-0 text-xs text-white/30 whitespace-nowrap hidden lg:inline">{sourceInfo}</span>
              )}
            </div>
            {/* Row 2: Actions */}
            <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
              <Button size="sm" onClick={loadProducts} disabled={isLoading}
                className="flex-shrink-0 gap-1.5 bg-emerald-500 text-white hover:bg-emerald-600">
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Cargando...' : 'Recargar'}
              </Button>
              <Button size="sm" variant="outline" onClick={syncProducts} disabled={isSyncing}
                className="flex-shrink-0 gap-1.5 bg-white/10 text-white hover:bg-white/20">
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
              </Button>
              <Button size="sm" variant="outline" onClick={clearCache} disabled={isClearing}
                className="flex-shrink-0 gap-1.5 bg-red-500/20 text-red-200 hover:bg-red-500/30">
                <Trash2 className="h-4 w-4" />
                {isClearing ? 'Limpiando...' : 'Limpiar caché'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsDebugOpen(true)}
                className="flex-shrink-0 gap-1.5 bg-purple-500/20 text-purple-200 hover:bg-purple-500/30">
                <Bug className="h-4 w-4" /> Debug
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60 flex items-center gap-1.5">
                <DollarSign className="h-3.5 w-3.5 text-white/30" />
                Valor Total Inventario
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-emerald-400 tabular-nums">
                {isLoading ? <Skeleton className="h-7 w-28 bg-white/20" /> : formatCurrency(totalInventoryValue)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60 flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-white/30" />
                Total Productos
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">
                {isLoading ? <Skeleton className="h-7 w-20 bg-white/20" /> : totalProducts.toLocaleString('es-ES')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60 flex items-center gap-1.5">
                <Boxes className="h-3.5 w-3.5 text-white/30" />
                Unidades en Stock
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-blue-400 tabular-nums">
                {isLoading ? <Skeleton className="h-7 w-20 bg-white/20" /> : totalInventoryUnits.toLocaleString('es-ES')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4">
              <CardDescription className="text-xs text-white/60 flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-white/30" />
                Costo Promedio / Unidad
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-amber-400 tabular-nums">
                {isLoading ? <Skeleton className="h-7 w-24 bg-white/20" /> : formatCurrency(averageCostPerUnit)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Búsqueda Específica de Costos */}
        <Card className="mt-4 border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="py-4 px-5">
            <div className="flex flex-row items-center gap-3">
              <div className="min-w-0">
                <CardTitle className="text-sm font-semibold text-white">Búsqueda Específica de Costos</CardTitle>
                <CardDescription className="text-xs text-white/50 mt-0.5">Busca por colección o producto individual para ver costos en tiempo real</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            {/* Search type + ID type toggles */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSearchType('collection')}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${searchType === 'collection' ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600' : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                <Tag className="h-3 w-3" /> Por Colección
              </button>
              <button onClick={() => setSearchType('product')}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${searchType === 'product' ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600' : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                <Package className="h-3 w-3" /> Por Producto
              </button>
              <div className="w-px bg-white/10 mx-1" />
              <button onClick={() => setSearchIdType('title')}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${searchIdType === 'title' ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                <FileText className="h-3 w-3" /> Por Nombre
              </button>
              <button onClick={() => setSearchIdType('id')}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${searchIdType === 'id' ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                <Hash className="h-3 w-3" /> Por ID
              </button>
            </div>

            {/* Search input */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <Input
                  placeholder={
                    searchType === 'collection'
                      ? searchIdType === 'id' ? 'Ej: 292198776921' : 'Ej: Conjunto pure'
                      : searchIdType === 'id' ? 'Ej: 7970902278233' : 'Ej: Pure Knockout Jacket'
                  }
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSpecificSearch() }}
                  className="pl-9 border-white/20 bg-white/10 text-white placeholder:text-white/30"
                />
              </div>
              <Button onClick={handleSpecificSearch} disabled={isSearching || !searchValue.trim()}
                className="gap-1.5 bg-emerald-500 text-white hover:bg-emerald-600">
                {isSearching
                  ? <><RefreshCw className="h-4 w-4 animate-spin" /> Buscando...</>
                  : <><Search className="h-4 w-4" /> Buscar</>}
              </Button>
            </div>

            {/* Search results */}
            {hasSearched && (
              <div className="rounded-xl border border-white/10 bg-white/5">
                <div className="px-4 py-3 border-b border-white/10">
                  <p className="text-xs font-medium text-white/60 uppercase tracking-wide">
                    {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
                  </p>
                </div>
                {searchResults.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {searchResults.map((product) => {
                      const totalInventory = product.variants?.reduce((sum: number, v: any) => sum + (v.inventory_quantity || 0), 0) || product.inventory || 0
                      const variantsWithStock = product.variants?.filter((v: any) => (v.inventory_quantity || 0) > 0) || []
                      return (
                        <div key={product.id} className="px-4 py-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white truncate">{product.title}</p>
                              <p className="text-xs text-white/40 mt-0.5">ID: {product.id}</p>
                              <div className="mt-3 flex flex-wrap gap-4">
                                <div>
                                  <p className="text-xs text-white/50">Costo Promedio</p>
                                  <p className="text-base font-bold text-emerald-400 tabular-nums">{formatCurrency(product.cost || 0)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-white/50">Inventario</p>
                                  <p className="text-base font-bold text-blue-400 tabular-nums">{totalInventory} uds</p>
                                </div>
                                <div>
                                  <p className="text-xs text-white/50">Valor Inventario</p>
                                  <p className="text-base font-bold text-amber-400 tabular-nums">{formatCurrency((product.cost || 0) * totalInventory)}</p>
                                </div>
                              </div>
                              {variantsWithStock.length > 0 && (
                                <div className="mt-3 rounded-lg border border-white/10 bg-white/5 overflow-hidden">
                                  <div className="px-3 py-2 border-b border-white/5">
                                    <p className="text-xs font-medium text-white/60">Variantes con stock ({variantsWithStock.length})</p>
                                  </div>
                                  <div className="divide-y divide-white/5">
                                    {variantsWithStock.map((variant: any) => (
                                      <div key={variant.id} className="flex items-center justify-between px-3 py-2 text-xs">
                                        <span className="text-white/70">{variant.title || 'Default'}</span>
                                        <div className="flex gap-4">
                                          <span className="text-emerald-400 tabular-nums">{formatCurrency(parseFloat(variant.cost || 0))}</span>
                                          <span className="text-blue-400 tabular-nums">{variant.inventory_quantity || 0} uds</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            {product.imageUrl && (
                              <img src={product.imageUrl} alt={product.title}
                                className="h-20 w-20 flex-shrink-0 rounded-lg border border-white/10 object-cover" />
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-white/40">No se encontraron resultados</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters + Search Bar */}
        <Card className="mt-4 border-white/10 bg-white/5 text-white backdrop-blur">
          <CardContent className="p-4 space-y-3">
            {/* Search + view mode */}
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                <Input
                  placeholder={viewMode === 'collections' ? 'Buscar colección...' : 'Buscar producto...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-white/10 text-white border-white/20 placeholder:text-white/30"
                />
              </div>
              <Select value={viewMode} onValueChange={(value: 'collections' | 'products') => setViewMode(value)}>
                <SelectTrigger className="flex-shrink-0 w-40 bg-white/10 text-white border-white/20 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 text-white border-white/20">
                  <SelectItem value="collections">Por Colección</SelectItem>
                  <SelectItem value="products">Por Producto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Collection selector (products view only) */}
            {viewMode === 'products' && (
              <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                <SelectTrigger className="bg-white/10 text-white border-white/20">
                  <SelectValue placeholder="Todas las colecciones" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/20">
                  <SelectItem value="all" className="text-white">Todas las colecciones</SelectItem>
                  {collectionsData.map((collection) => (
                    <SelectItem key={collection.id} value={collection.id} className="text-white">{collection.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Filter toggles */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-white/40 uppercase tracking-wide flex items-center gap-1"><Filter className="h-3 w-3" /> Costo</span>
                <div className="flex gap-1.5">
                  {([
                    { key: 'all', label: 'Todos', active: 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' },
                    { key: 'with-cost', label: 'Con costo', active: 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600' },
                    { key: 'without-cost', label: 'Sin costo', active: 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600' },
                  ] as const).map(({ key, label, active }) => (
                    <button key={key} onClick={() => setCostFilter(key)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${costFilter === key ? active : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-white/40 uppercase tracking-wide flex items-center gap-1"><Filter className="h-3 w-3" /> Estado</span>
                <div className="flex gap-1.5">
                  {([
                    { key: 'all', label: 'Todos', active: 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' },
                    { key: 'active', label: 'Activos', active: 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600' },
                    { key: 'archived', label: 'Archivados', active: 'bg-red-500 text-white border-red-500 hover:bg-red-600' },
                  ] as const).map(({ key, label, active }) => (
                    <button key={key} onClick={() => setStatusFilter(key)}
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${statusFilter === key ? active : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {(costFilter !== 'all' || statusFilter !== 'all') && (
                <button onClick={() => { setCostFilter('all'); setStatusFilter('all') }}
                  className="inline-flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors mt-4">
                  <X className="h-3 w-3" /> Limpiar filtros
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="mt-4 border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="py-4 px-5">
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-sm font-semibold text-white">
                  {viewMode === 'collections' ? 'Costos por Colección' : 'Costos por Producto'}
                </CardTitle>
                <CardDescription className="text-xs text-white/50 mt-0.5">
                  {viewMode === 'collections'
                    ? `${filteredCollections.length} colecciones`
                    : `${filteredProducts.length} productos`}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-px">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-none bg-white/5" />
                ))}
              </div>
            ) : viewMode === 'collections' ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Colección</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Productos</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Inventario</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Costo Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredCollections.length === 0 ? (
                      <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-white/40">No se encontraron colecciones</td></tr>
                    ) : filteredCollections.map((collection) => (
                      <tr key={collection.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-5 py-3 font-medium text-white whitespace-nowrap">{collection.title}</td>
                        <td className="px-5 py-3 text-right text-white/70 tabular-nums whitespace-nowrap">{collection.productsCount}</td>
                        <td className="px-5 py-3 text-right text-blue-400 tabular-nums whitespace-nowrap">{collection.totalInventory.toLocaleString('es-ES')}</td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-400 tabular-nums whitespace-nowrap">{formatCurrency(collection.totalCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-5 py-2.5 text-left text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Producto</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Inventario</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Costo Unitario</th>
                      <th className="px-5 py-2.5 text-right text-xs font-medium text-white/50 uppercase tracking-wide whitespace-nowrap">Costo Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredProducts.length === 0 ? (
                      <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-white/40">No se encontraron productos</td></tr>
                    ) : filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-5 py-3 font-medium text-white whitespace-nowrap max-w-xs truncate">{product.title}</td>
                        <td className="px-5 py-3 text-right text-blue-400 tabular-nums whitespace-nowrap">{(product.inventory || 0).toLocaleString('es-ES')}</td>
                        <td className="px-5 py-3 text-right text-white/70 tabular-nums whitespace-nowrap">{formatCurrency(product.cost || 0)}</td>
                        <td className="px-5 py-3 text-right font-semibold text-emerald-400 tabular-nums whitespace-nowrap">{formatCurrency((product.inventory || 0) * (product.cost || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Debug Dialog */}
        <Dialog open={isDebugOpen} onOpenChange={setIsDebugOpen}>
          <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto bg-gray-900/95 text-white border-white/20 backdrop-blur-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Bug className="h-5 w-5 text-purple-400" /> Debug — Costos e Inventario
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Herramientas de diagnóstico e importación de productos
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-2">
              {/* Depurar producto */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <h3 className="mb-1 text-sm font-semibold text-white flex items-center gap-2">
                  <Search className="h-4 w-4 text-white/40" /> Depurar Producto Específico
                </h3>
                <p className="mb-3 text-xs text-white/50">Busca por título o ID para ver datos en Firestore y Shopify</p>
                <div className="flex gap-2">
                  <Input placeholder="Ej: Pure Knockout Jacket Brown 19477"
                    value={debugProductSearch}
                    onChange={(e) => setDebugProductSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && debugSpecificProduct()}
                    className="flex-1 border-white/20 bg-white/10 text-white placeholder:text-white/30" />
                  <Button onClick={debugSpecificProduct} disabled={isDebugLoading}
                    className="bg-emerald-500 text-white hover:bg-emerald-600">
                    {isDebugLoading ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>
                {debugProductData && (
                  <div className="mt-4 space-y-3">
                    <div className="rounded-lg bg-black/30 p-3 text-xs">
                      <p className="font-semibold text-white/70 mb-1">Búsqueda: <span className="text-white">{debugProductData.search}</span></p>
                      <p className="text-white/40">{new Date(debugProductData.timestamp).toLocaleString('es-ES')}</p>
                    </div>
                    <div className="rounded-lg bg-black/30 p-3">
                      <p className="text-sm font-semibold mb-2">
                        Firestore:{' '}
                        <span className={debugProductData.foundInFirestore ? 'text-emerald-400' : 'text-red-400'}>
                          {debugProductData.foundInFirestore
                            ? <><CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />Encontrado</>
                            : <><AlertCircle className="inline h-3.5 w-3.5 mr-1" />No encontrado</>}
                        </span>
                      </p>
                      {debugProductData.firestoreData && (
                        <pre className="overflow-auto rounded bg-black/50 p-2 text-xs text-green-300">
                          {JSON.stringify(debugProductData.firestoreData, null, 2)}
                        </pre>
                      )}
                    </div>
                    {debugProductData.shopifyData && (
                      <div className="rounded-lg bg-black/30 p-3">
                        <p className="text-sm font-semibold mb-2 text-emerald-400">Shopify API</p>
                        <pre className="overflow-auto rounded bg-black/50 p-2 text-xs text-green-300">
                          {JSON.stringify(debugProductData.shopifyData, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Importar */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <h3 className="mb-1 text-sm font-semibold text-white flex items-center gap-2">
                  <Upload className="h-4 w-4 text-white/40" /> Importar Productos con Costos
                </h3>
                <p className="mb-3 text-xs text-white/50">
                  Obtén productos desde Shopify con costos y guárdalos en Firestore.
                  <span className="block mt-0.5 text-blue-300">Recomendado: usa el botón automático</span>
                </p>
                <div className="space-y-2">
                  <Button onClick={handleImportFromShopify} disabled={isDebugLoading}
                    className="w-full gap-2 bg-emerald-500 text-white hover:bg-emerald-600">
                    <Upload className="h-4 w-4" /> Importar Automáticamente desde Shopify
                  </Button>
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="text-xs text-white/30">O</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                  <Button onClick={handleImportJSON} disabled={isDebugLoading} variant="outline"
                    className="w-full gap-2 bg-white/5 text-white hover:bg-white/10 border-white/10">
                    <FolderOpen className="h-4 w-4" /> Seleccionar Archivo JSON Manual
                  </Button>
                  {debugProductData?.importStatus && (
                    <div className={`rounded-lg p-3 mt-2 ${debugProductData.importStatus.success ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                      <p className={`text-sm font-semibold flex items-center gap-2 ${debugProductData.importStatus.success ? 'text-emerald-400' : 'text-red-400'}`}>
                        {debugProductData.importStatus.success
                          ? <CheckCircle2 className="h-4 w-4" />
                          : <AlertCircle className="h-4 w-4" />}
                        {debugProductData.importStatus.message}
                      </p>
                      {debugProductData.importStatus.details && (
                        <pre className="mt-2 overflow-auto rounded bg-black/50 p-2 text-xs text-white/70">
                          {JSON.stringify(debugProductData.importStatus.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-1">
                <h3 className="text-sm font-semibold text-white mb-2">Estadísticas</h3>
                <p className="text-xs text-white/60">Total productos: <span className="text-white font-medium">{products.length}</span></p>
                <p className="text-xs text-white/60">Con inventario: <span className="text-white font-medium">{products.filter(p => p.inventory > 0).length}</span></p>
                <p className="text-xs text-white/60">Con costo definido: <span className="text-emerald-400 font-medium">{products.filter(p => (p.cost || 0) > 0).length}</span></p>
                <p className="text-xs text-white/60">Con colecciones: <span className="text-white font-medium">{products.filter(p => p.collections && p.collections.length > 0).length}</span></p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-white">Ejemplo (primeros 5 productos)</h3>
                <pre className="overflow-auto rounded-lg bg-black/50 p-4 text-xs text-green-300">
                  {JSON.stringify(products.slice(0, 5), null, 2)}
                </pre>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-white">Colecciones detectadas</h3>
                <pre className="overflow-auto rounded-lg bg-black/50 p-4 text-xs text-green-300">
                  {JSON.stringify(collectionsData, null, 2)}
                </pre>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default CostsDashboard
